import { useMemo } from 'react';
import { useCurrentAppMonth } from '../../hooks/useCurrentAppMonth';
import { useBudgetWindow } from '../../store/Budget/BudgetContext';
import { getVisibleMonths, getWindowMonths, isBudgetRangeActive, scenarioMonths } from './helpers';

/**
 * CALL-815: the two month lists every Budget OVERVIEW grid renders from — the summary, the handling
 * data/costs grid and the on-top costs grid.
 *
 *  - `months` is the view: the 12-month window, or every month of the `Datum von`–`bis` range when one
 *    is set, however many that is. Anything that COMPUTES reads this one (row completeness,
 *    extrapolation, the pinned total).
 *  - `visibleMonths` is the same list with "Vergangene Monate ausblenden" applied — columns only.
 *
 * Extracted because the three-line pair below was copy-pasted in five files and the range may reach
 * only three of them: the other two are the Prices & ABT grids (BonusGrid, useRateGrid), which share
 * the window and the hide-past checkbox but have no range control of their own. They keep calling
 * getWindowMonths directly, so the range cannot leak into them by a later edit to a shared helper.
 *
 * The range's month generator is the scenario period's (scenarioMonths) — same job, same inclusive
 * ends, and it already matches the backend's generateMonthsBetween.
 */
export const useOverviewMonths = (hidePastMonths: boolean) => {
    const { windowStart, rangeFrom, rangeTo } = useBudgetWindow();
    // CALL-781: the cutoff is state, not a read of the clock, so the columns follow a month rollover on
    // a page left open (see useCurrentAppMonth).
    const currentMonth = useCurrentAppMonth();

    const months = useMemo(
        () =>
            isBudgetRangeActive(rangeFrom, rangeTo) ? scenarioMonths(rangeFrom, rangeTo) : getWindowMonths(windowStart),
        [windowStart, rangeFrom, rangeTo]
    );

    const visibleMonths = useMemo(
        () => getVisibleMonths(months, hidePastMonths, currentMonth),
        [months, hidePastMonths, currentMonth]
    );

    return { months, visibleMonths };
};
